import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { PageWrapper } from '../../../styledComponents/wrappers';
import { StripedDataGrid } from '../../../styledComponents/datagrid';
import FulfillmentStatusChip from '../FulfillmentStatusChip';
import { getPackQueue } from '../../../services/fulfillment/packApi';
import { PackQueueItem } from '../../../services/fulfillment/types';
import { ORDER_TYPE_LABELS } from '../../../services/fulfillment/constants';
import { FormatDateString } from '../../../utils/helper';

export const MOCK_PACK_QUEUE_ROUTE = '/fulfillment/mock/pack';

const ageDays = (createdAt: string) =>
  Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));

const MockPackQueue: React.FC = () => {
  const history = useHistory();
  const [orders, setOrders] = useState<PackQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPackQueue()
      .then(setOrders)
      .catch(() => setError('Failed to load pack queue.'))
      .finally(() => setLoading(false));
  }, []);

  const sorted = useMemo(
    () => [...orders].sort((a, b) => a.shopifyOrderName.localeCompare(b.shopifyOrderName)),
    [orders],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        headerName: 'Order #',
        field: 'shopifyOrderName',
        flex: 0.8,
        renderCell: (params: GridRenderCellParams<PackQueueItem>) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{params.row.shopifyOrderName}</span>
            {params.row.isPriority && (
              <Box
                sx={{
                  px: 0.75,
                  py: 0.125,
                  bgcolor: '#fef08a',
                  color: '#713f12',
                  borderRadius: 0.5,
                  fontSize: 10,
                  fontWeight: 700,
                  border: '1px solid #fde047',
                  lineHeight: '16px',
                }}
              >
                PRIORITY
              </Box>
            )}
          </Box>
        ),
      },
      {
        headerName: 'Type',
        field: 'orderType',
        flex: 0.7,
        valueFormatter: ({ value }) =>
          ORDER_TYPE_LABELS[value as PackQueueItem['orderType']] ?? value,
      },
      {
        headerName: 'Items',
        field: 'lineItemCount',
        flex: 0.4,
        type: 'number',
      },
      {
        headerName: 'Status',
        field: 'lifecycleStatus',
        flex: 0.9,
        renderCell: (params: GridRenderCellParams<PackQueueItem>) => (
          <FulfillmentStatusChip
            lifecycleStatus={params.row.lifecycleStatus}
            waveStatus="no_wave"
            waveId={null}
          />
        ),
      },
      {
        headerName: 'Order date',
        field: 'createdAt',
        flex: 0.8,
        valueFormatter: ({ value }) => (value ? FormatDateString(value) : ''),
      },
      {
        headerName: 'Age',
        field: 'age',
        flex: 0.5,
        sortable: false,
        renderCell: (params: GridRenderCellParams<PackQueueItem>) => {
          const days = ageDays(params.row.createdAt);
          return (
            <Tooltip title={days > 3 ? 'Order is over 3 days old' : ''}>
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: 12,
                  fontWeight: days > 3 ? 700 : 400,
                  bgcolor: days > 3 ? '#fef2f2' : 'transparent',
                  color: days > 3 ? '#b91c1c' : 'text.secondary',
                  border: days > 3 ? '1px solid #fecaca' : 'none',
                }}
              >
                {days}d
              </Box>
            </Tooltip>
          );
        },
      },
    ],
    [],
  );

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Pack Queue
        <Box
          component="span"
          sx={{
            ml: 1.5,
            fontSize: 12,
            fontWeight: 400,
            color: 'grey.500',
            verticalAlign: 'middle',
          }}
        >
          MOCK
        </Box>
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <StripedDataGrid
          rows={sorted}
          columns={columns}
          getRowId={row => row.shopifyOrderId}
          autoHeight
          onRowClick={(params: GridRowParams) =>
            history.push(`/fulfillment/mock/pack/${(params.row as PackQueueItem).shopifyOrderId}`)
          }
          sx={{ cursor: 'pointer' }}
          rowHeight={56}
          pageSizeOptions={[15, 30]}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          getRowClassName={params =>
            (params.row as PackQueueItem).lifecycleStatus === 'packing' ? 'packing-row' : ''
          }
        />
      )}
    </PageWrapper>
  );
};

export default MockPackQueue;
